import { Button } from "@/components/ui/button";
import { DialogHeader, DialogFooter } from "@/components/ui/dialog";
import { FieldGroup, Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TimeSheetDetails } from "@/v2/api/time-sheet/FindOneTimeSheetLog";
import SaveTimeSheet from "@/v2/api/time-sheet/SaveTimeSheet";
import {
    Dialog,
    DialogTrigger,
    DialogContent,
    DialogTitle,
    DialogDescription,
    DialogClose,
} from "@/components/ui/dialog";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState } from "react";

export default function AddOtherDeductionDialog({
    initialAmount,
    initialReason,
    onSave,
    timeSheetId,
    timeSheetDetails,
}: {
    initialAmount: number;
    initialReason: string;
    onSave: (data: { amount: number; reason: string }) => void;
    timeSheetId: string;
    timeSheetDetails: TimeSheetDetails;
}) {
    const [amount, setAmount] = useState(initialAmount.toString());
    const [reason, setReason] = useState(initialReason);

    const [open, setOpen] = useState(false);

    const queryClient = useQueryClient();

    const { mutate, isPending } = useMutation({
        mutationKey: ["time-sheets-logs", timeSheetId],
        mutationFn: () =>
            SaveTimeSheet({
                timeSheetId: timeSheetId,
                data: {
                    title: timeSheetDetails.title,
                    status: timeSheetDetails.status,
                    description: timeSheetDetails.description,
                    note: timeSheetDetails.note,
                    deduction_note: reason,
                    other_deductions: parseFloat(amount) || 0,
                },
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["time-sheets-logs", timeSheetId],
            });
        },
        onSettled: () => {
            setOpen(false);
        },
    });

    const handleSave = () => {
        const numericAmount = parseFloat(amount) || 0;
        onSave({ amount: numericAmount, reason });
        mutate();
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline">Other Deduction</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-sm">
                <DialogHeader>
                    <DialogTitle>Add Other Deductions</DialogTitle>
                    <DialogDescription>
                        Here You can add additional Deduction if occured
                    </DialogDescription>
                </DialogHeader>
                <FieldGroup>
                    <Field>
                        <Label htmlFor="deduction_amount">
                            Deduction Amount
                        </Label>
                        <Input
                            id="deduction_amount"
                            name="deduction_amount"
                            type="number"
                            value={amount}
                            step={0.01}
                            onKeyDown={(e) =>
                                ["e", "E", "+", "-"].includes(e.key) &&
                                e.preventDefault()
                            }
                            onChange={(e) => setAmount(e.target.value)}
                        />
                    </Field>
                    <Field>
                        <Label htmlFor="deduction_reason">Deduction Note</Label>
                        <Textarea
                            id="deduction_reason"
                            name="deduction_reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                        />
                    </Field>
                </FieldGroup>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button
                        onClick={handleSave}
                        disabled={isPending}
                        className="w-28"
                    >
                        {isPending ? <Loader2 /> : "Save Changes"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
